import type { MapPoint } from '@/lib/maps/types';

import type { ImportedShapes } from './schemas';

/**
 * The parse result, carried from `<MapPage>` across the route boundary to the **Import preview** —
 * **in document memory only** ([ADR-0016](../../../docs/adr/0016-map-view-state.md)'s lifetime,
 * applied rather than re-decided).
 *
 * The choice of *where* is the whole point. React Router `state` is the obvious reach and is wrong:
 * it goes into `history.pushState`, and browsers **restore `history.state` on reload** — so after
 * F5 `useLocation().state` would still hold the shape, contradicting "when I reload the page … no
 * shape is retained and I start from the empty state" head-on. A module variable dies with the
 * document and no two documents share one, so **that criterion holds by construction**: there is no
 * persistence to misconfigure, so it cannot regress. Re-POSTing on arrival is worse still — the
 * `File` cannot cross the boundary without a store either, and it charges the Grower a second parse.
 *
 * In `features/fields` rather than `lib/` because it holds {@link ImportedShapes} data whose types
 * belong beside `schemas.ts`, and `eslint-plugin-boundaries` permits every consumer
 * (`pages → feature` via the public `index`).
 *
 * Imperative and deliberately **not reactive**, the same shape as `lib/maps/viewport-store.ts` and
 * `lib/api/token-store.ts`: nothing subscribes, and both consumers read once at mount the way
 * `useFieldPoints`' `initialPoints` does.
 *
 * **Reads are non-destructive.** A read-clears "take once" was rejected outright: it breaks under
 * StrictMode's double-invoked `useState` initializer, which is the very hook doing the reading. The
 * store is cleared by the surfaces that **end** a session instead — `<MapPage>` on mount, since
 * every Cancel / Discard / Save path lands there.
 *
 * `placed` (the committed placement) is the store's second slot in grilling decision 3. It lands
 * with ticket 07, whose "Place Field" writes it and whose `<FieldMappingScreen>` reads it.
 */
let shapes: ImportedShapes | null = null;
let placed: readonly MapPoint[] | null = null;
let placedName: string | null = null;

export const importStore = {
  read: (): ImportedShapes | null => shapes,

  save: (next: ImportedShapes): void => {
    shapes = next;
  },

  /**
   * The positioned shape, handed from **Placement mode** to **Field Mapping mode** across the
   * `routes.fieldImport` → `routes.fieldCreate` boundary (grilling decision 3's second slot).
   *
   * A **separate slot from `shapes`**, not a mutation of it, because `routes.fieldCreate` is also
   * reached from _Add Boundaries in Map_ — where this must read `null`. A non-null `placed` at that
   * page's mount **is** the "this is an imported session" signal, which is what ticket 08 keys the
   * 200-**Point allowance** on.
   */
  readPlaced: (): readonly MapPoint[] | null => placed,

  /**
   * The chosen **Imported shape**'s `suggestedName`, written by the same call that writes
   * {@link importStore.savePlaced} so the two can never describe different shapes.
   *
   * A second slot rather than a field on `placed`: `placed`'s type is read by ticket 08's `origin`
   * derivation and by `useFieldPoints`' `initialPoints`, and widening it would churn both for a
   * value neither reads. Optional on the writer, so the placement tests that only care about
   * geometry stay as they are.
   */
  readPlacedName: (): string | null => placedName,

  savePlaced: (next: readonly MapPoint[], suggestedName: string | null = null): void => {
    placed = next;
    placedName = suggestedName;
  },

  /**
   * Called by the surface that ends an import session — `<MapPage>` on mount — and by tests, which
   * must reset between cases because module state outlives one. Precisely what
   * `mapViewportStore.clear()` exists for.
   *
   * **All three slots, always.** They belong to one session: a Discard from Placement mode and a
   * successful Save Field both land on the Map, and leaving any slot behind would seed the next
   * visit to `routes.fieldCreate` with the shape from a session the Grower already ended.
   */
  clear: (): void => {
    shapes = null;
    placed = null;
    placedName = null;
  },
};
